import React, { useState, useEffect, useRef } from 'react';
import { 
  Truck, 
  Search, 
  Filter, 
  CheckCircle, 
  Clock,
  MapPin,
  Eye,
  RefreshCw,
  User,
  Phone,
  Package,
  Calendar,
  AlertTriangle,
  Navigation,
  TrendingUp,
  BarChart3,
  X,
  Edit,
  Save,
  XCircle
} from 'lucide-react';
import io from 'socket.io-client';
import { API_BASE_URL } from '../../api';
import './AdminDeliveryManagement.css';

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `$${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const str = String(value).replace(/_/g, ' ').toLowerCase();
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const AdminDeliveryManagement = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDetailsFor, setShowDetailsFor] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [socket, setSocket] = useState(null);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    outForDelivery: 0,
    delivered: 0,
    pending: 0,
    averageDeliveryTime: 0
  });
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [deliveryAgent, setDeliveryAgent] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const socketRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // Fetch deliveries (orders that are ready or out for delivery)
  const fetchDeliveries = async (pageParam = currentPage) => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const params = new URLSearchParams();
      
      // Only fetch orders that are ready for delivery or out for delivery
      if (filter === 'all') {
        params.append('status', 'ready,out_for_delivery,delivered');
      } else {
        params.append('status', filter);
      }
      
      if (selectedVendorFilter) params.append('vendor_id', selectedVendorFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom && dateTo) { 
        params.append('date_from', dateFrom); 
        params.append('date_to', dateTo); 
      }
      
      const pageToFetch = typeof pageParam === 'number' ? pageParam : 1;
      params.append('page', String(pageToFetch));
      params.append('limit', String(rowsPerPage));

      const response = await fetch(`${API_BASE_URL}/api/admin/orders?${params.toString()}`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.orders)
            ? data.orders
            : [];
        
        // Filter to only delivery-related orders
        const deliveryOrders = list.filter(order => 
          ['ready', 'out_for_delivery', 'delivered'].includes(order.status)
        );
        
        setDeliveries(deliveryOrders);
        
        if (data?.pagination) {
          setPaginationInfo(data.pagination);
          const serverPage = parseInt(data.pagination.current_page, 10);
          if (!Number.isNaN(serverPage) && serverPage > 0) {
            setCurrentPage(serverPage);
          }
        }
        
        // Calculate stats
        calculateStats(deliveryOrders);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (orders) => {
    const total = orders.length;
    const outForDelivery = orders.filter(o => o.status === 'out_for_delivery').length;
    const delivered = orders.filter(o => o.status === 'delivered').length;
    const pending = orders.filter(o => o.status === 'ready').length;
    
    // Calculate average delivery time (if delivered_at and out_for_delivery_at exist)
    let totalDeliveryTime = 0;
    let deliveryCount = 0;
    orders.forEach(order => {
      if (order.delivered_at && order.out_for_delivery_at) {
        const delivered = new Date(order.delivered_at).getTime();
        const outForDelivery = new Date(order.out_for_delivery_at).getTime();
        if (!Number.isNaN(delivered) && !Number.isNaN(outForDelivery)) {
          totalDeliveryTime += (delivered - outForDelivery) / (1000 * 60); // minutes
          deliveryCount++;
        }
      }
    });
    
    const averageDeliveryTime = deliveryCount > 0 
      ? Math.round(totalDeliveryTime / deliveryCount) 
      : 0;

    setStats({
      totalDeliveries: total,
      outForDelivery,
      delivered,
      pending,
      averageDeliveryTime
    });
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
        headers: { 'x-admin-key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      }
    } catch (e) {
      console.error('Error fetching order details:', e);
    }
  };

  const fetchVendors = async () => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`${API_BASE_URL}/api/admin/vendors`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const vendors = data.items || data.vendors || (Array.isArray(data) ? data : []);
        setVendors(vendors.filter(vendor => vendor.status === 'APPROVED'));
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
    if (apiKey) {
      const socketUrl = API_BASE_URL || window.location.origin;
      const newSocket = io(socketUrl, {
        auth: { token: apiKey, type: 'admin' }
      });
      
      newSocket.on('connect', () => {
        console.log('Admin connected to WebSocket for deliveries');
        newSocket.emit('join-admin');
      });
      
      newSocket.on('order-update', (orderData) => {
        if (['ready', 'out_for_delivery', 'delivered'].includes(orderData.status)) {
          setDeliveries(prev => {
            const exists = prev.some(o => o.id === orderData.id);
            if (exists) {
              return prev.map(order => order.id === orderData.id ? { ...order, ...orderData } : order);
            }
            if (['ready', 'out_for_delivery'].includes(orderData.status)) {
              return [orderData, ...prev];
            }
            return prev;
          });
          fetchDeliveries(currentPage);
        }
      });
      
      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Polling fallback
  useEffect(() => {
    const interval = setInterval(() => {
      const connected = !!socketRef.current && socketRef.current.connected;
      if (!connected) {
        fetchDeliveries(currentPage);
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [currentPage]);

  useEffect(() => {
    fetchDeliveries(1);
    fetchVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchDeliveries(1);
  }, [filter, selectedVendorFilter, dateFrom, dateTo]);

  // Update delivery status
  const updateDeliveryStatus = async (orderId, status) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchDeliveries(currentPage);
        setEditingDelivery(null);
        setDeliveryAgent('');
        setDeliveryNotes('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update delivery status');
      }
    } catch (error) {
      console.error('Error updating delivery status:', error);
      alert('Failed to update delivery status');
    }
  };

  const handleAssignDelivery = async (order) => {
    if (!deliveryAgent.trim()) {
      alert('Please enter delivery agent name');
      return;
    }

    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      // First update status to out_for_delivery
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${order.id}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: 'out_for_delivery',
          notes: `Assigned to: ${deliveryAgent}. ${deliveryNotes || ''}`
        })
      });
      
      if (response.ok) {
        fetchDeliveries(currentPage);
        setEditingDelivery(null);
        setDeliveryAgent('');
        setDeliveryNotes('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to assign delivery');
      }
    } catch (error) {
      console.error('Error assigning delivery:', error);
      alert('Failed to assign delivery');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'ready': 'status-ready',
      'out_for_delivery': 'status-out-for-delivery',
      'delivered': 'status-delivered'
    };
    return statusClasses[status] || 'status-default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <Package size={16} />;
      case 'out_for_delivery': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'ready': 'Ready for Delivery',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered'
    };
    return statusTexts[status] || status;
  };

  const filteredDeliveries = deliveries.filter(delivery => {
    const matchesSearch = delivery.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (delivery.customer_name && delivery.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (delivery.shipping_address && delivery.shipping_address.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filter === 'all' || delivery.status === filter;
    return matchesSearch && matchesFilter;
  });

  const readyDeliveries = deliveries.filter(d => d.status === 'ready');
  const activeDeliveries = deliveries.filter(d => d.status === 'out_for_delivery');
  const completedDeliveries = deliveries.filter(d => d.status === 'delivered');
  
  const visibleDeliveries = activeTab === 'ready'
    ? readyDeliveries
    : activeTab === 'active'
      ? activeDeliveries
      : activeTab === 'completed'
        ? completedDeliveries
        : filteredDeliveries;

  const totalDeliveries = paginationInfo?.total_orders ?? deliveries.length;
  const totalPages = paginationInfo?.total_pages ?? (deliveries.length > 0 ? 1 : 0);

  return (
    <div className="admin-delivery-management">
      <div className="admin-delivery-header">
        <div className="header-content">
          <h1>
            <Truck size={32} style={{ marginRight: 12 }} />
            Delivery Management
          </h1>
          <p>Track and manage all deliveries across vendors with real-time updates</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => fetchDeliveries(currentPage)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="delivery-stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3b82f6' }}>
            <Truck size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDeliveries}</div>
            <div className="stat-label">Total Deliveries</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <Package size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Ready for Delivery</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <Navigation size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.outForDelivery}</div>
            <div className="stat-label">Out for Delivery</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#8b5cf6' }}>
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.delivered}</div>
            <div className="stat-label">Delivered</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#06b6d4' }}>
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.averageDeliveryTime}m</div>
            <div className="stat-label">Avg Delivery Time</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-delivery-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <Eye size={16} />
          All Deliveries ({deliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'ready' ? 'active' : ''}`}
          onClick={() => setActiveTab('ready')}
        >
          <Package size={16} />
          Ready ({readyDeliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <Truck size={16} />
          In Transit ({activeDeliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={16} />
          Completed ({completedDeliveries.length})
        </button>
      </div>

      {/* Filters */}
      <div className="admin-delivery-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by order number, customer, address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchDeliveries(1);
                }
              }}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <Filter size={20} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <select
              value={selectedVendorFilter}
              onChange={(e) => setSelectedVendorFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.business_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filters-right">
          <div className="date-range">
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)} 
              placeholder="From"
            />
            <span style={{ margin: '0 4px' }}>to</span>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)} 
              placeholder="To"
            />
          </div>
          <div className="results-count">
            {totalDeliveries > 0
              ? `${visibleDeliveries.length} of ${totalDeliveries} deliveries`
              : '0 deliveries'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading deliveries...</div>
      ) : (
        <div className="admin-delivery-list" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Vendor</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Delivery Address</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleDeliveries.map((delivery) => (
                <tr key={delivery.id}>
                  <td style={{ fontWeight: 600 }}>{delivery.order_number}</td>
                  <td>{delivery.vendor_name || 'N/A'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <User size={14} />
                      {delivery.customer_name || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Phone size={14} />
                      {delivery.customer_phone || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 300 }}>
                      <MapPin size={14} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {delivery.shipping_address || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td>{delivery.item_count || 0} items</td>
                  <td style={{ fontWeight: 600 }}>{formatCurrency(delivery.total_amount)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(delivery.status)}`}>
                      {getStatusIcon(delivery.status)}
                      {getStatusText(delivery.status)}
                    </span>
                  </td>
                  <td>{formatDateTime(delivery.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {delivery.status === 'ready' && (
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setEditingDelivery(delivery);
                            setDeliveryAgent('');
                            setDeliveryNotes('');
                          }}
                        >
                          <Truck size={16} />
                          Assign
                        </button>
                      )}
                      {delivery.status === 'out_for_delivery' && (
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={() => updateDeliveryStatus(delivery.id, 'delivered')}
                        >
                          <CheckCircle size={16} />
                          Mark Delivered
                        </button>
                      )}
                      <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={() => {
                          setShowDetailsFor(prev => prev === delivery.id ? null : delivery.id);
                          if (!orderDetails[delivery.id]) fetchOrderDetails(delivery.id);
                        }}
                      >
                        <Eye size={16} />
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {showDetailsFor && orderDetails[showDetailsFor] && (
            <div className="delivery-items" style={{ marginTop: 16 }}>
              <div className="items-header">Order Details</div>
              <div className="order-info-grid">
                <div>
                  <strong>Order Number:</strong> {orderDetails[showDetailsFor].order_number}
                </div>
                <div>
                  <strong>Customer:</strong> {orderDetails[showDetailsFor].customer_name}
                </div>
                <div>
                  <strong>Phone:</strong> {orderDetails[showDetailsFor].customer_phone}
                </div>
                <div>
                  <strong>Address:</strong> {orderDetails[showDetailsFor].shipping_address}
                </div>
                <div>
                  <strong>Total Amount:</strong> {formatCurrency(orderDetails[showDetailsFor].total_amount)}
                </div>
                <div>
                  <strong>Status:</strong> {formatTitleCase(orderDetails[showDetailsFor].status)}
                </div>
              </div>
              <div className="items-header" style={{ marginTop: 16 }}>Products</div>
              {(orderDetails[showDetailsFor].items || []).map(it => (
                <div key={it.id} className="item-row">
                  <div className="item-name">{it.product_name || `#${it.product_id}`}</div>
                  <div className="item-qty">x{it.quantity}</div>
                  <div className="item-price">{formatCurrency(it.unit_price)}</div>
                  <div className="item-total">{formatCurrency(it.unit_price * it.quantity)}</div>
                </div>
              ))}
            </div>
          )}

          {visibleDeliveries.length === 0 && (
            <div className="empty-state">
              <Truck size={48} />
              <h3>No deliveries found</h3>
              <p>No deliveries match your current filters</p>
            </div>
          )}

          {visibleDeliveries.length > 0 && (
            <div className="pagination-container" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {visibleDeliveries.length} of {totalDeliveries} deliveries (page {currentPage} of {Math.max(totalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage > 1) {
                      const prevPage = currentPage - 1;
                      setCurrentPage(prevPage);
                      fetchDeliveries(prevPage);
                    }
                  }}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`btn btn-secondary btn-sm ${page === currentPage ? 'active' : ''}`}
                      style={page === currentPage ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' } : {}}
                      onClick={() => {
                        if (page !== currentPage) {
                          setCurrentPage(page);
                          fetchDeliveries(page);
                        }
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const nextPage = currentPage + 1;
                      setCurrentPage(nextPage);
                      fetchDeliveries(nextPage);
                    }
                  }}
                  disabled={currentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(newSize);
                    setCurrentPage(1);
                    fetchDeliveries(1);
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assign Delivery Modal */}
      {editingDelivery && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Assign Delivery</h2>
              <button className="close-btn" onClick={() => {
                setEditingDelivery(null);
                setDeliveryAgent('');
                setDeliveryNotes('');
              }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p><strong>Order:</strong> {editingDelivery.order_number}</p>
              <p><strong>Customer:</strong> {editingDelivery.customer_name}</p>
              <p><strong>Address:</strong> {editingDelivery.shipping_address}</p>
              <p><strong>Amount:</strong> {formatCurrency(editingDelivery.total_amount)}</p>
              
              <div className="form-group">
                <label>Delivery Agent Name *</label>
                <input
                  type="text"
                  value={deliveryAgent}
                  onChange={(e) => setDeliveryAgent(e.target.value)}
                  placeholder="Enter delivery agent name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Delivery Notes</label>
                <textarea
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  rows="3"
                  placeholder="Add any special delivery instructions..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setEditingDelivery(null);
                  setDeliveryAgent('');
                  setDeliveryNotes('');
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => handleAssignDelivery(editingDelivery)}
                disabled={!deliveryAgent.trim()}
              >
                <Truck size={16} />
                Assign & Dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeliveryManagement;

